import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, SearchIcon } from '@/shared/icons';
import { cn } from '@/shared/lib/cn';
import { focusRingClassNameStrong, formControlSurfaceClassName } from '@/shared/config/theme';

export interface SearchableSelectOption {
  readonly id: string;
  readonly label: string;
}

interface SearchableSelectProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly SearchableSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyOptionLabel?: string;
  allowEmpty?: boolean;
  hasError?: boolean;
  disabled?: boolean;
  listboxAriaLabel?: string;
  listMaxHeightClassName?: string;
}

const PANEL_Z_INDEX = 110;
const PANEL_GAP_PX = 4;

export default function SearchableSelect({
  id: idProp,
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyOptionLabel,
  allowEmpty = true,
  hasError = false,
  disabled = false,
  listboxAriaLabel,
  listMaxHeightClassName = 'max-h-64',
}: SearchableSelectProps) {
  const { t } = useTranslation('common');
  const resolvedPlaceholder = placeholder ?? t('select');
  const resolvedSearchPlaceholder = searchPlaceholder ?? t('search');
  const resolvedEmptyOptionLabel = emptyOptionLabel ?? t('noSubject');
  const resolvedListboxAriaLabel = listboxAriaLabel ?? t('optionsAria');
  const generatedId = useId();
  const inputId = idProp ?? generatedId;
  const listboxId = `${inputId}-listbox`;
  const anchorRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [panelStyle, setPanelStyle] = useState<CSSProperties>({});

  const selectedLabel = useMemo(
    () => options.find((option) => option.id === value)?.label,
    [options, value],
  );

  const filteredOptions = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) {
      return options;
    }
    return options.filter((option) => option.label.toLowerCase().includes(normalized));
  }, [options, query]);

  const updatePanelPosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) {
      return;
    }
    const rect = anchor.getBoundingClientRect();
    setPanelStyle({
      position: 'fixed',
      top: rect.bottom + PANEL_GAP_PX,
      left: rect.left,
      width: rect.width,
      zIndex: PANEL_Z_INDEX,
    });
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    updatePanelPosition();
    const handleReposition = () => updatePanelPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    requestAnimationFrame(() => searchRef.current?.focus());
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open, updatePanelPosition]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (anchorRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
      setQuery('');
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const handleSelect = (nextValue: string) => {
    onChange(nextValue);
    setOpen(false);
    setQuery('');
  };

  const triggerLabel = selectedLabel ?? resolvedPlaceholder;

  const panel = open ? (
    <div
      ref={panelRef}
      style={panelStyle}
      className="overflow-hidden rounded-lg border border-gray-300 bg-white shadow-lg"
      role="listbox"
      id={listboxId}
      aria-label={resolvedListboxAriaLabel}
    >
      <div className="border-b border-gray-100 p-2">
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            ref={searchRef}
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={resolvedSearchPlaceholder}
            className={cn(
              'h-9 w-full rounded-md border border-gray-300 pl-8 pr-3 text-sm',
              formControlSurfaceClassName,
              focusRingClassNameStrong,
            )}
            aria-label={resolvedSearchPlaceholder}
          />
        </div>
      </div>
      <ul className={cn(listMaxHeightClassName, 'overflow-y-auto py-1')}>
        {allowEmpty ? (
          <li>
            <button
              type="button"
              role="option"
              aria-selected={value === ''}
              onClick={() => handleSelect('')}
              className={cn(
                'flex w-full px-3 py-2 text-left text-sm transition-colors hover:bg-gray-50',
                value === '' ? 'bg-brand-50/60 font-medium text-gray-900' : 'text-gray-700',
              )}
            >
              {resolvedEmptyOptionLabel}
            </button>
          </li>
        ) : null}
        {filteredOptions.length === 0 ? (
          <li className="px-3 py-3 text-center text-sm text-gray-500">{t('noMatchesGeneric')}</li>
        ) : (
          filteredOptions.map((option) => (
            <li key={option.id}>
              <button
                type="button"
                role="option"
                aria-selected={value === option.id}
                onClick={() => handleSelect(option.id)}
                className={cn(
                  'flex w-full px-3 py-2 text-left text-sm transition-colors hover:bg-gray-50',
                  value === option.id
                    ? 'bg-brand-50/60 font-medium text-gray-900'
                    : 'text-gray-700',
                )}
              >
                {option.label}
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  ) : null;

  return (
    <>
      <div ref={anchorRef} className="relative w-full">
        <button
          id={inputId}
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          onClick={() => {
            if (disabled) {
              return;
            }
            setOpen((previous) => !previous);
          }}
          className={cn(
            'flex h-10 min-h-10 w-full items-center justify-between gap-2 rounded-lg border border-gray-300 px-3 text-left text-sm leading-normal',
            formControlSurfaceClassName,
            focusRingClassNameStrong,
            hasError && 'border-red-500 focus:ring-red-500',
            disabled && 'cursor-not-allowed opacity-60',
            !selectedLabel && 'text-gray-400',
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronDownIcon
            className={cn(
              'h-4 w-4 shrink-0 text-gray-500 transition-transform',
              open && 'rotate-180',
            )}
            aria-hidden
          />
        </button>
      </div>
      {panel ? createPortal(panel, document.body) : null}
    </>
  );
}
